/**
 * The savings bonus — the parent's side of it, which is the only side it has.
 *
 * This is the most dangerous screen in the app, and the danger is invisible at
 * the moment of the mistake: a rate picked because it makes the number move
 * fast enough to feel exciting is a bill that arrives a year later, long after
 * anyone remembers choosing it. So the projection is on screen before the save
 * button, always, updating as the field is typed — not behind a confirm step
 * that a parent learns to tap through.
 *
 * The copy says who funds it. A bank pays interest out of what it earns lending
 * the money on; there is no such engine here, and the honest sentence is that
 * the household is paying. The word "interest" belongs to this screen and the
 * grown-up who reads it — the child's screen calls it a bonus and names the
 * family (CLAUDE.md, "Lead with match, not interest").
 */

import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useFamily } from '../lib/family.jsx';
import { BackLink } from '../ui/Nav.jsx';
import { EMPTY_BALANCE, setSavingsRule, useBank } from '../lib/bank.js';
import { PLAUSIBLE_ANNUAL_RATE_PCT, projectLiability } from '../domain/liability.js';
import { centsFromDollars, formatCents } from '../domain/money.js';

const dollars = (cents) => (cents / 100).toFixed(2);

export default function SavingsRule() {
  const { childId } = useParams();
  const { family, kids, profile } = useFamily();
  const bank = useBank(family?.id);
  const navigate = useNavigate();
  // `null` means "whatever the saved rule says" — so the fields fill themselves
  // in when the rule finishes loading, without an effect racing the typing.
  const [rate, setRate] = useState(null);
  const [cap, setCap] = useState(null);
  const [from, setFrom] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Setting what the household will pay is a grown-up's decision, and a child
  // who could set their own bonus rate has a money printer.
  if (profile) return <Navigate to="/" replace />;

  const child = kids.find((k) => k.id === childId);
  const rule = bank.rules[childId] ?? null;
  const balance = bank.byChild[childId]?.balance ?? EMPTY_BALANCE;

  const rateValue = rate ?? String(rule?.annualRatePct ?? 0);
  const capValue = cap ?? (rule?.monthlyCapCents != null ? dollars(rule.monthlyCapCents) : '');
  const fromValue = from ?? bank.today;

  const annualRatePct = Number(rateValue) || 0;
  const monthlyCapCents = String(capValue).trim() ? centsFromDollars(capValue) : null;

  // A year of it, on what they have saved today. The weekly deposit is zero on
  // purpose: guessing what a child will save turns a projection into a forecast,
  // and the number that matters here is the one the rate alone produces.
  const projection = projectLiability({
    balanceCents: balance.save,
    weeklyDepositCents: 0,
    annualRatePct,
    monthlyCapCents,
    months: 12,
  });

  async function save(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await setSavingsRule({
        familyId: family.id,
        childId,
        annualRatePct,
        monthlyCapCents,
        effectiveFrom: fromValue,
      });
      navigate(`/bank/${childId}`, { replace: true });
    } catch (err) {
      setError(`That rule didn’t save — ${err.message}.`);
      setBusy(false);
    }
  }

  return (
    <main className="app-shell stack">
      <div className="stack tight">
        <BackLink to={`/bank/${childId}`}>{child?.display_name ?? 'Bank'}</BackLink>
        <h1>Savings bonus</h1>
      </div>

      <p className="muted">
        Money in {child?.display_name ?? 'their'} Save bucket grows a little each month, and
        you are the one paying for it. {child?.display_name ?? 'They'} will see it as a bonus
        from the family, which is what it is.
      </p>

      {bank.error && <p className="notice error">{bank.error}</p>}
      {error && <p className="notice error" role="alert">{error}</p>}

      <form className="stack" onSubmit={save}>
        <div className="card stack">
          <div className="row">
            <label style={{ flex: '1 1 8rem' }}>
              Rate a year (percent)
              <input
                type="number" inputMode="decimal" min="0" step="0.5" required
                value={rateValue} onChange={(e) => setRate(e.target.value)}
              />
            </label>
            <label style={{ flex: '1 1 8rem' }}>
              Most per month (dollars, optional)
              <input
                type="number" inputMode="decimal" min="0" step="0.5" placeholder="No cap"
                value={capValue} onChange={(e) => setCap(e.target.value)}
              />
            </label>
          </div>
          <label>
            Starting from
            <input type="date" value={fromValue} onChange={(e) => setFrom(e.target.value)} />
          </label>
          {/*
            A cap in dollars rather than as a percentage, for the reason a rate
            cap fails: a ceiling expressed as a rate stops bounding anything
            once the balance is large.
          */}
          <p className="small muted">
            A cap is the ceiling on what you pay in any one month. Leave it empty for none.
          </p>
        </div>

        {/*
          The liability, in the parent's own money, before the button. This is
          the whole reason the screen exists.
        */}
        <div className="card stack tight">
          <p className="eyebrow">What this costs you</p>
          <p>
            At this rate, {child?.display_name ?? 'they'} has about{' '}
            <strong className="money">{formatCents(projection.totalCents)}</strong> in a year,
            of which <strong className="money">{formatCents(projection.growthCents)}</strong> is
            bonus you pay.
          </p>
          <p className="small muted">
            Worked out on the {formatCents(balance.save)} in Save today, with nothing added.
            Anything they put in on top grows too.
          </p>
        </div>

        {annualRatePct > PLAUSIBLE_ANNUAL_RATE_PCT && (
          <p className="notice warn">
            A real bank pays far less than this. It compounds month on month, so the amount you
            owe grows faster the longer it sits — it is your money to promise, and worth
            picking with that in mind.
          </p>
        )}

        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save this rate'}
        </button>
      </form>

      {rule && (
        <p className="small muted">
          Now: {Number(rule.annualRatePct)}% a year
          {rule.monthlyCapCents != null && `, at most ${formatCents(rule.monthlyCapCents)} a month`}
          {rule.effectiveFrom && `, since ${rule.effectiveFrom}`}. Saving a new rate leaves the old
          one in place for the history it already explains.
        </p>
      )}
    </main>
  );
}
